import { Component, computed, input } from '@angular/core';
import { BaseFormControl, BaseSignalInput, SignalRadio } from '@vallift/ngl';

/** Collection-backed answer payload (matches dropdown CollectionSelectAnswer). */
export type ChoiceCollectionData = {
  id: string;
  uniqueKey: string;
  display: string;
  secondaryDisplay?: string;
};

export interface ChoiceOption {
  value: string;
  label: string;
  description?: string;
  /** When set, selecting this option writes the full collection object as the value. */
  data?: ChoiceCollectionData;
}

function isChoiceCollectionData(value: unknown): value is ChoiceCollectionData {
  return (
    typeof value === 'object' &&
    value != null &&
    !Array.isArray(value) &&
    'id' in value &&
    typeof (value as ChoiceCollectionData).id === 'string'
  );
}

@Component({
  selector: 'lib-signal-single-choice',
  imports: [BaseSignalInput],
  templateUrl: './single-choice.html',
  styleUrl: './single-choice.css',
})
export class SingleChoice extends BaseFormControl {
  readonly label = input('');
  /** Supporting text shown directly under the field label. */
  readonly description = input('');
  options = input.required<ChoiceOption[]>();

  /** Disabled and readonly both block interaction. Only disabled fades the options. */
  readonly isInteractionBlocked = computed(
    () => this.disabled() || this.readonly(),
  );

  selectOption(option: ChoiceOption): void {
    if (this.isInteractionBlocked()) {
      return;
    }
    if (option.data) {
      this.value.set({
        id: option.data.id,
        uniqueKey: option.data.uniqueKey,
        display: option.data.display,
        ...(option.data.secondaryDisplay != null
          ? { secondaryDisplay: option.data.secondaryDisplay }
          : {}),
      });
      return;
    }
    this.value.set(option.value);
  }

  isSelected(option: ChoiceOption): boolean {
    const current = this.value();
    if (isChoiceCollectionData(current)) {
      return current.id === option.value;
    }
    return current === option.value;
  }
}
